type Row = Record<string, any>;

export type FieldDeletePendingOperation = {
  id: string;
  operation: string;
  collection: string;
  itemId: string;
  data?: unknown;
};

export type FieldDeleteDependencyInput = {
  fieldId: string;
  companyId: string;
  deleteOperationId: string;
  fieldTable: string;
  checklistTable: string;
  valuesTable: string;
};

export type FieldDeleteDependencyServices = {
  pending(): Promise<FieldDeletePendingOperation[]>;
  localValues(fieldId: string, companyId: string): Promise<Row[]>;
};

const parsePayload = (data: unknown): Row | null => {
  try {
    const payload = typeof data === 'string' ? JSON.parse(data) : data;
    return payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : null;
  } catch {
    return null;
  }
};

// Aguarda a fila dependente; a saida pode ser ACK remoto ou resultado terminal contabilizado.
export const verifyFieldDeleteDependencies = async (
  input: FieldDeleteDependencyInput,
  deps: FieldDeleteDependencyServices
): Promise<{ safe: boolean; reason: string }> => {
  const pending = await deps.pending();
  const pendingParentIds = new Set<string>();
  for (const operation of pending) {
    if (operation.id === input.deleteOperationId) continue;
    if (operation.collection === input.fieldTable && operation.itemId === input.fieldId) {
      return { safe: false, reason: 'operacao_do_campo_pendente' };
    }
    if (operation.collection === input.checklistTable && operation.operation === 'create') {
      pendingParentIds.add(operation.itemId);
    }
    if (operation.collection !== input.valuesTable) continue;
    const payload = parsePayload(operation.data);
    if (!payload) return { safe: false, reason: 'operacao_de_valor_sem_payload_valido' };
    if (operation.operation === 'checklist_partial_update'
      && Object.prototype.hasOwnProperty.call(payload, input.fieldId)) {
      return { safe: false, reason: 'resposta_parcial_pendente' };
    }
    if (payload.campo_id === input.fieldId) {
      return { safe: false, reason: 'resposta_pendente' };
    }
  }

  if (pendingParentIds.size > 0) {
    const local = await deps.localValues(input.fieldId, input.companyId);
    if (local.some(row => !row?.id || !row?.checklist_id || row.empresa_id !== input.companyId
      || row.campo_id !== input.fieldId)) {
      return { safe: false, reason: 'resposta_local_invalida' };
    }
    if (local.some(row => pendingParentIds.has(String(row.checklist_id)))) {
      return { safe: false, reason: 'pai_local_pendente' };
    }
  }

  return { safe: true, reason: 'dependencias_sem_pendencia' };
};
